import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { FieldLabel, FieldHint, LinkButton, SecondaryButton } from '../../ui/common';
import { LABEL_LOGO_TYPES, labelLogoFileError } from '../../utils/labelLogo';

// Logo na etykietach (24.10.2026): podgląd tego, co trafi na naklejkę, skąd pochodzi
// i (dla administratora) wgranie osobnego logo etykiet albo powrót do logo z „Danych firmy”.
// Zapis idzie od razu na serwer (PUT /api/contract-settings z samym `label_logo`), bo logo
// jest ustawieniem firmy, a nie tego jednego wydruku.

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

// Białe pole jak naklejka: logo ogląda się tu na tle, na którym zostanie wydrukowane.
const Tile = styled.div`
  width: 120px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px dashed var(--input-border);
  background: #fff;
  display: grid;
  place-items: center;
  padding: 6px;
  box-sizing: border-box;
  color: #6b7280;
  font-size: var(--fs-xs, 0.75rem);
  text-align: center;

  img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Source = styled.span`
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const Toggle = styled.label`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
  font-size: var(--fs-sm);
  cursor: pointer;

  &[aria-disabled='true'] { opacity: 0.55; cursor: default; }
`;

export default function LabelLogoPicker({ logo, loaded, isAdmin, showLogo, onShowLogoChange, onSaved }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const zapisz = async (labelLogo) => {
    setBusy(true);
    setError('');
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/contract-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ label_logo: labelLogo }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('labels.logo.saveError'));
      onSaved(labelLogo);
    } catch (err) {
      setError(err.message || t('labels.logo.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const wybierz = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const blad = labelLogoFileError(file);
    if (blad) {
      setError(blad === 'size' ? t('labels.logo.tooBig') : t('labels.logo.wrongType'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => zapisz(String(reader.result));
    reader.onerror = () => setError(t('labels.logo.readError'));
    reader.readAsDataURL(file);
  };

  const zrodlo = !loaded
    ? t('labels.logo.loading')
    : logo.source === 'label'
      ? t('labels.logo.sourceLabel')
      : logo.source === 'company'
        ? t('labels.logo.sourceCompany')
        : t('labels.logo.sourceNone');

  return (
    <div data-cy="labels-logo">
      <FieldLabel as="span">{t('labels.logo.title')}</FieldLabel>
      <Row>
        <Tile aria-hidden={logo.src ? undefined : 'true'}>
          {logo.src
            ? <img src={logo.src} alt={t('labels.logo.previewAlt')} data-cy="labels-logo-preview" />
            : t('labels.logo.empty')}
        </Tile>
        <Side>
          <Source data-cy="labels-logo-source">{zrodlo}</Source>
          {isAdmin ? (
            <Actions>
              <SecondaryButton
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy || !loaded}
                data-cy="labels-logo-upload"
              >
                {busy ? t('labels.logo.saving') : (logo.source === 'label' ? t('labels.logo.replace') : t('labels.logo.upload'))}
              </SecondaryButton>
              {logo.source === 'label' && (
                <LinkButton onClick={() => zapisz(null)} disabled={busy} data-cy="labels-logo-reset">
                  {t('labels.logo.useCompany')}
                </LinkButton>
              )}
              <input
                ref={inputRef}
                type="file"
                accept={LABEL_LOGO_TYPES.join(',')}
                onChange={wybierz}
                hidden
                data-cy="labels-logo-file"
              />
            </Actions>
          ) : (
            <Source>{t('labels.logo.adminOnly')}</Source>
          )}
        </Side>
      </Row>
      {error && <FieldHint role="alert" style={{ color: 'var(--danger)' }}>{error}</FieldHint>}
      <FieldHint>{t('labels.logo.hint')}</FieldHint>
      <Toggle aria-disabled={!logo.src ? 'true' : undefined}>
        <input
          type="checkbox"
          checked={showLogo && !!logo.src}
          disabled={!logo.src}
          onChange={(e) => onShowLogoChange(e.target.checked)}
          data-cy="labels-show-logo"
        />
        {t('labels.logo.print')}
      </Toggle>
    </div>
  );
}
